import {
  CheckInput,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import { Fragment, ReactChild, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { SectionTitle } from '../../../../../components/text';
import { useValidations } from '../../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../../locales/translations';
import { formatToOnlyPositiveIntegers } from '../../../../../utils/currency';
import { useCourseFormLoaderData } from '../../hooks/CourseFormLoader';

export interface CourseFormValues {
  academicLevel: SelectOptionType | null;
  course: SelectOptionType | null;
  name: string;
  code: string;
  modality: string;
  school: string;
  totalHours: number;
  activityHours: { [activityTypeId: number]: number };
  description: string;
  hasCertificate: boolean;
}

export const getDefaultValues = (): CourseFormValues => ({
  academicLevel: null,
  course: null,
  name: '',
  code: '',
  modality: '',
  school: '',
  totalHours: 0,
  activityHours: {},
  description: '',
  hasCertificate: false,
});

export interface CourseFormProps {
  children: (props: { isSubmitting: boolean }) => ReactChild;
  onSubmit: (
    values: CourseFormValues,
  ) => Promise<'CONNECTION' | 'HTTP_ERROR' | undefined>;
  initialValues?: Partial<CourseFormValues>;
  isEditMode?: boolean;
  isInActiveOffer?: boolean;
}

export function CourseForm(props: CourseFormProps) {
  const {
    onSubmit: onSubmitProp,
    children,
    initialValues,
    isEditMode,
    isInActiveOffer,
  } = props;
  const { courses, activityTypes, academicLevels } = useCourseFormLoaderData();

  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );
  const prefix = 'courseForm';

  const methods = useForm<CourseFormValues>({
    mode: 'onChange',
    defaultValues: { ...getDefaultValues(), ...initialValues },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    setValue,
    watch,
    reset,
  } = methods;

  const { msgValidations, rulesPatternDecimal } = useValidations();

  const [selectedAcademicLevel, selectedCourse, activityHoursValues] = watch([
    'academicLevel',
    'course',
    'activityHours',
  ]);

  // Handle academic level filtering
  useEffect(() => {
    if (selectedCourse && selectedAcademicLevel) {
      const course = courses.find((c) => c.id === selectedCourse.value);
      if (course && course.academicLevel?.id !== selectedAcademicLevel.value) {
        setValue('course', null);
      }
    }
  }, [selectedAcademicLevel, selectedCourse, courses, setValue]);

  // Auto-fill academic level in edit mode based on initial course
  useEffect(() => {
    if (isEditMode && initialValues?.course && !selectedAcademicLevel) {
      const course = courses.find((c) => c.id === initialValues.course?.value);
      if (course && course.academicLevel) {
        const foundAcademicLevel = academicLevels.find(
          (al) => al.value === course.academicLevel!.id,
        );
        if (foundAcademicLevel) {
          setValue('academicLevel', foundAcademicLevel);
        }
      }
    }
  }, [
    isEditMode,
    initialValues,
    courses,
    academicLevels,
    selectedAcademicLevel,
    setValue,
  ]);

  // Reset form when initialValues changes (e.g., when data is fetched)
  useEffect(() => {
    if (initialValues) {
      reset({ ...getDefaultValues(), ...initialValues });
    }
  }, [initialValues, reset]);

  // Update form fields when a course is selected
  useEffect(() => {
    if (!isEditMode) {
      if (!selectedCourse) {
        setValue('name', '');
        setValue('code', '');
        setValue('modality', '');
        setValue('school', '');
      }

      if (selectedCourse) {
        const course = courses.find((c) => c.id === selectedCourse.value);
        if (course) {
          setValue('name', course.name);
          setValue('code', course.code);
          setValue('modality', course.modality?.name || '');
          setValue('school', course.school?.name || '');
        }
      }
    }
  }, [isEditMode, selectedCourse, courses, setValue]);

  // Calculate total hours based on activity hours
  const totalHours = useMemo(() => {
    if (!activityHoursValues) {
      return 0;
    }
    return Object.values(activityHoursValues).reduce(
      (sum, hours) => sum + Number(hours || 0),
      0,
    );
  }, [activityHoursValues]);

  useEffect(() => {
    setValue('totalHours', totalHours);
  }, [totalHours, setValue]);

  const courseOptions = useMemo(() => {
    let filteredCourses = courses;
    if (selectedAcademicLevel) {
      filteredCourses = courses.filter(
        (c) => c.academicLevel?.id === selectedAcademicLevel.value,
      );
    }
    return filteredCourses.map((course) => ({
      label: `${course.code} - ${course.name}`,
      value: course.id,
    }));
  }, [courses, selectedAcademicLevel]);

  const onSubmit = async (formValues: CourseFormValues) => {
    await onSubmitProp(formValues);
  };

  return (
    <Fragment>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col xs={12}>
              <Row className="mb-4">
                <Col xs={12}>
                  <SectionTitle text={t(`${prefix}.title`)} />
                  <p>{t(`${prefix}.subtitle`)}</p>
                  <Row>
                    <Col xs={12} md={6} className="pb-3">
                      <Select
                        name="academicLevel"
                        label={t(`${prefix}.academicLevel`, 'Nivel Académico')}
                        options={academicLevels}
                        control={control}
                        rules={{ required: msgValidations.required }}
                        disabled={isEditMode || isInActiveOffer}
                      />
                    </Col>
                    <Col xs={12} md={6} className="pb-3">
                      <Select
                        name="course"
                        label={t(`${prefix}.course`)}
                        options={courseOptions}
                        control={control}
                        rules={{ required: msgValidations.required }}
                        disabled={
                          isEditMode ||
                          isInActiveOffer ||
                          !selectedAcademicLevel
                        }
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} md={6} className="pb-3">
                      <TextInput
                        name="name"
                        label={t(`${prefix}.name`)}
                        control={control}
                        rules={{
                          required: msgValidations.required,
                          maxLength: {
                            value: 255,
                            message: t('common.validations.maxLength', {
                              length: 255,
                            }),
                          },
                        }}
                        disabled={isInActiveOffer}
                      />
                    </Col>
                    <Col xs={12} md={6} className="pb-3">
                      <TextInput
                        name="code"
                        label={t(`${prefix}.code`)}
                        control={control}
                        rules={{
                          required: msgValidations.required,
                          maxLength: {
                            value: 50,
                            message: t('common.validations.maxLength', {
                              length: 50,
                            }),
                          },
                        }}
                        disabled={isInActiveOffer}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} md={6} className="pb-3">
                      <TextInput
                        name="modality"
                        label={t(`${prefix}.modality`)}
                        control={control}
                        disabled
                      />
                    </Col>
                    <Col xs={12} md={6} className="pb-3">
                      <TextInput
                        name="school"
                        label={t(`${prefix}.school`)}
                        control={control}
                        disabled
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} md={6} className="pb-3">
                      <TextInput
                        name="totalHours"
                        label={t(`${prefix}.totalHours`)}
                        control={control}
                        disabled
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>

              {activityTypes.length > 0 && (
                <Row className="mb-4">
                  <Col xs={12}>
                    <SectionTitle text={t(`${prefix}.activityHoursTitle`)} />
                  </Col>
                  {activityTypes.map((activityType) => (
                    <Col xs={12} md={6} key={activityType.id} className="pb-3">
                      <TextInput
                        name={`activityHours.${activityType.id}` as any}
                        control={control}
                        label={activityType.name}
                        rules={{
                          required: msgValidations.required,
                          pattern: rulesPatternDecimal,
                        }}
                        formatter={formatToOnlyPositiveIntegers}
                        disabled={isInActiveOffer}
                      />
                    </Col>
                  ))}
                </Row>
              )}

              <Row className="mb-4">
                <Col xs={12}>
                  <SectionTitle text={t(`${prefix}.description`)} />
                </Col>
                <Col xs={12} className="pb-3">
                  <TextAreaInput
                    name="description"
                    label={''}
                    control={control}
                    maxLength={200}
                    height={100}
                    showCharCounter
                    disabled={isInActiveOffer}
                  />
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12}>
                  <SectionTitle text={t(`${prefix}.certificate`)} />
                </Col>
                <Col xs={12} md={6} className="pb-3">
                  <CheckInput
                    name="hasCertificate"
                    label={t(`${prefix}.hasCertificate`)}
                    control={control}
                    disabled={isInActiveOffer}
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          {children({ isSubmitting })}
        </Form>
      </FormProvider>
    </Fragment>
  );
}
